import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../lib/format";

interface ClientStat {
  id: number; name: string; email: string | null;
  language: "en" | "th"; credit_terms_days: number;
  open_count: number; open_total: number; last_sent_at: string | null;
}

export default function Dashboard() {
  const { current } = useCurrentCompany();
  const [stats, setStats] = useState<ClientStat[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!current) return;
    setLoading(true);
    api<{ clients: ClientStat[] }>(`/api/clients?company_id=${current.id}`)
      .then((r) => setStats(r.clients.filter((c) => c.open_count > 0)))
      .finally(() => setLoading(false));
  }, [current]);

  const totals = stats.reduce(
    (acc, c) => { acc.count += c.open_count; acc.amount += c.open_total; return acc; },
    { count: 0, amount: 0 },
  );

  const sorted = stats.slice().sort((a, b) => b.open_total - a.open_total);
  const currency = current?.currency || "THB";

  return (
    <div className="space-y-7">
      <div>
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Dashboard</h1>
            <p className="mt-1 text-sm text-slate-500">{current?.name || "—"}</p>
          </div>
          <Link to="/send" className="btn-primary">
            Send reminders
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" /></svg>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="Clients with open balance"
          value={String(stats.length)}
          accent="from-sky-50 to-white"
        />
        <MetricCard
          label="Open invoices"
          value={String(totals.count)}
          accent="from-indigo-50 to-white"
        />
        <MetricCard
          label="Total outstanding"
          value={fmtMoney(totals.amount, currency)}
          accent="from-violet-50 to-white"
          emphasis
        />
      </div>

      <section className="card overflow-hidden">
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div>
            <h2 className="section-title">Top open balances</h2>
            <p className="text-xs text-slate-500">Sorted by outstanding amount, largest first</p>
          </div>
          <Link to="/clients" className="text-xs font-medium text-slate-500 hover:text-slate-900">
            View all clients →
          </Link>
        </header>
        <table className="refined">
          <thead>
            <tr>
              <th>Client</th>
              <th>Lang</th>
              <th>Open</th>
              <th className="text-right">Total</th>
              <th>Last reminder</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                <div className="font-medium">All clear.</div>
                <div className="text-xs text-slate-400">No open invoices. Import a new QBO export to refresh.</div>
              </td></tr>
            )}
            {sorted.slice(0, 12).map((c) => (
              <tr key={c.id}>
                <td>
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.email || <span className="text-rose-500">missing email</span>}</div>
                </td>
                <td><span className="pill pill-slate uppercase">{c.language}</span></td>
                <td className="text-slate-700">{c.open_count}</td>
                <td className="text-right font-mono font-semibold text-slate-900">{fmtMoney(c.open_total, currency)}</td>
                <td className="text-xs text-slate-500">{c.last_sent_at ? fmtDate(c.last_sent_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function MetricCard({
  label, value, accent, emphasis,
}: {
  label: string;
  value: string;
  accent: string;
  emphasis?: boolean;
}) {
  return (
    <div className={`card card-hover relative overflow-hidden p-5`}>
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent} opacity-70`} />
      <div className="relative">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`mt-2 ${emphasis ? "text-3xl" : "text-2xl"} font-semibold tracking-tightish text-slate-900`}>
          {value}
        </div>
      </div>
    </div>
  );
}
